/**
 * Structured symbolic analysis — detects motifs before any prose is written.
 */

const FAMILY = {
  initiative: [1],
  cooperation: [2],
  expression: [3],
  structure: [4],
  change: [5],
  care: [6],
  reflection: [7],
  power: [8],
  completion: [9],
};

const FAMILY_LABEL = {
  initiative: 'independence and direct action',
  cooperation: 'relationship nuance and sensitivity',
  expression: 'communication and creative shaping',
  structure: 'order, rhythm, and reliability',
  change: 'movement, curiosity, and adaptation',
  care: 'responsibility and emotional attunement',
  reflection: 'inner processing and discernment',
  power: 'material reality, boundaries, and impact',
  completion: 'integration and broader perspective',
};

function baseDigit(d) {
  const v = Number(d);
  if (v === 11 || v === 22 || v === 33) {
    return v === 11 ? 2 : v === 22 ? 4 : 6;
  }
  return Math.min(9, Math.max(1, v));
}

function familyForDigit(d) {
  const digit = baseDigit(d);
  const entry = Object.entries(FAMILY).find(([, arr]) => arr.includes(digit));
  return entry ? entry[0] : null;
}

function scoreFamilies(tally) {
  const scores = {};
  Object.keys(FAMILY).forEach((k) => {
    scores[k] = 0;
  });
  Object.keys(tally).forEach((dStr) => {
    const d = Number(dStr);
    const fam = familyForDigit(d);
    if (fam) scores[fam] += tally[d].score;
  });
  return scores;
}

export function detectThemes(bundle, weighted) {
  const { tally } = weighted;
  const digits = Object.keys(tally).map(Number).sort((a, b) => tally[b].score - tally[a].score);

  const repeatedCoreDigits = [];
  const freq = {};
  const coreSources = ['lifePath', 'expression', 'soulUrge', 'personality', 'birthday', 'maturity'];
  coreSources.forEach((key) => {
    const v = bundle.numbers[key]?.value;
    if (v) {
      freq[v] = (freq[v] || 0) + 1;
    }
  });
  Object.keys(freq).forEach((d) => {
    if (freq[d] >= 2) repeatedCoreDigits.push(Number(d));
  });

  const familyScores = scoreFamilies(tally);
  const sortedFamilies = Object.keys(familyScores).sort((a, b) => familyScores[b] - familyScores[a]);

  const lp = bundle.numbers.lifePath.value;
  const su = bundle.numbers.soulUrge.value;
  const ex = bundle.numbers.expression.value;
  const pe = bundle.numbers.personality.value;
  const py = bundle.numbers.personalYear.value;

  const tensions = [];
  if (Math.abs(su - lp) >= 3) {
    tensions.push({
      id: 'desire_path_gap',
      summary: 'There may be a noticeable stretch between inner motivation and the longer arc described by the Life Path.',
      a: su,
      b: lp,
    });
  }
  if (Math.abs(pe - su) >= 3) {
    tensions.push({
      id: 'outer_inner_gap',
      summary: 'The outward style suggested by the Personality number may not always mirror the quieter pull of the Soul Urge.',
      a: pe,
      b: su,
    });
  }
  if (Math.abs(ex - lp) >= 3) {
    tensions.push({
      id: 'expression_path_gap',
      summary: 'The name pattern (Expression) and the Life Path may ask for different kinds of pacing.',
      a: ex,
      b: lp,
    });
  }

  const pyFamily = familyForDigit(py);
  const lpFamily = familyForDigit(lp);
  let timingResonance = 'neutral';
  if (pyFamily && lpFamily && pyFamily === lpFamily) {
    timingResonance = 'aligned';
  } else if (pyFamily && lpFamily) {
    const iy = sortedFamilies.indexOf(pyFamily);
    const il = sortedFamilies.indexOf(lpFamily);
    if (iy >= 0 && il >= 0 && iy < il) {
      timingResonance = 'supportive_build';
    }
  }

  const structuralType = (() => {
    const top = sortedFamilies[0];
    const second = sortedFamilies[1];
    if (familyScores[top] > familyScores[second] * 1.6) return `concentrated_${top}`;
    if (sortedFamilies.slice(0, 3).every((f) => familyScores[f] > 0)) return 'balanced_multi';
    return `dual_emphasis_${top}_${second}`;
  })();

  const chartBalance = (() => {
    const missing = bundle.missingNumbers?.length || 0;
    if (missing >= 4) return 'sparse';
    if (missing <= 1) return 'concentrated';
    return 'mixed';
  })();

  const supportiveOverlaps = [];
  if (repeatedCoreDigits.length) {
    supportiveOverlaps.push({
      id: 'repeated_digit',
      detail: `The digit ${repeatedCoreDigits.join(', ')} appears more than once among core indicators, which can make that theme easier to recognize in lived experience.`,
    });
  }

  return {
    structuralType,
    chartBalance,
    dominantDigits: digits.slice(0, 4),
    familyScores,
    familyLabels: FAMILY_LABEL,
    topFamilies: sortedFamilies.slice(0, 3),
    repeatedCoreDigits,
    tensions,
    timingResonance,
    personalYearFamily: pyFamily,
    lifePathFamily: lpFamily,
    supportiveOverlaps,
    karmicDebt: bundle.karmicDebtMarkers || [],
    missingNumbers: bundle.missingNumbers || [],
    masterEcho: [
      bundle.numbers.lifePath,
      bundle.numbers.expression,
      bundle.numbers.soulUrge,
      bundle.numbers.personality,
      bundle.numbers.personalYear,
    ].some((x) => x?.isMaster),
  };
}

export function buildDominantThemeRecords(themeContext) {
  const out = [];
  const { topFamilies, familyLabels, repeatedCoreDigits, tensions } = themeContext;

  topFamilies.slice(0, 3).forEach((fam) => {
    out.push({
      id: `family_${fam}`,
      title: `Emphasis on ${familyLabels[fam] || fam}`,
      why: `This theme gathers weight because several weighted indicators point toward the ${fam} family of qualities.`,
    });
  });

  repeatedCoreDigits.forEach((d) => {
    out.push({
      id: `repeat_${d}`,
      title: `A repeating ${d} motif`,
      why: `The number ${d} shows up more than once among core numbers, which often makes its qualities easier to notice as a through-line rather than a single trait.`,
    });
  });

  tensions.slice(0, 2).forEach((t) => {
    out.push({
      id: t.id,
      title: 'Pattern of contrast',
      why: t.summary,
    });
  });

  return out.slice(0, 8);
}
